import { Button } from '@material-ui/core';
import { useTheme } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import { GetApp } from '@material-ui/icons';
import Papa from 'papaparse';
import * as React from 'react';
import DocumentTitle from 'react-document-title';
import { normalizeUrl } from '../../common/lang/url';

import { PageDialogLink } from '../../common/page-dialog-link';
import { ClientCandidates } from '../../containers/client-candidates';
import { JobCandidatesBoard } from '../../containers/job-candidates-board';
import { useSlides } from '../../hooks/use-candidate-slides';
import { CellValue as DataPanelCellValue, DataPanel, DataPanelColumn, dataPanelContainerStyles } from '../data-panel';
import { AgentResultsHeader } from './agent-results-header';
import { getCellDisplayValue, isCellWithEntity, TableData, TableRow } from './agent-types';

interface AgentResultsTableProps {
    table: TableData;
    onOrbitClick?: () => void;
    className?: string;
}

const TABLE_PAGE_SIZE = 25;

// Convert table data to CSV format using papaparse
const convertTableToCSV = (table: TableData): string => {
    // Convert table rows to array format expected by papaparse
    const dataForCSV = table.rows.map((row) => {
        const rowData: Record<string, string | number> = {};
        table.columns.forEach((col) => {
            const cellValue = row[col.id];
            const displayValue = getCellDisplayValue(cellValue);
            rowData[col.label] = displayValue;
        });
        return rowData;
    });

    // Use papaparse to generate CSV with proper escaping
    return Papa.unparse(dataForCSV, {
        header: true,
        quotes: true
    });
};

// Trigger CSV file download
const downloadCSV = (csvContent: string, filename: string): void => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
};

export const AgentResultsTable: React.FC<AgentResultsTableProps> = ({ table, className }) => {
    const theme = useTheme<Theme>();
    const { setList } = useSlides();

    const handleDownloadCSV = React.useCallback(() => {
        const csvContent = convertTableToCSV(table);
        const sanitizedTitle = table.title.replace(/[^a-z0-9]/gi, '_').toLowerCase();
        const timestamp = new Date().toISOString().split('T')[0];
        const filename = `${sanitizedTitle}_${timestamp}.csv`;
        downloadCSV(csvContent, filename);
    }, [table]);

    // Transform TableData columns to DataPanel format
    const dataPanelColumns: Array<DataPanelColumn<string>> = React.useMemo(
        () =>
            table.columns.map((col) => ({
                id: col.id,
                label: col.label
            })),
        [table.columns]
    );

    // Extract cell value, handling CellWithEntity
    const getCellValue = React.useCallback(
        (columnId: string) =>
            (row: TableRow): DataPanelCellValue => {
                const value = row[columnId];
                return getCellDisplayValue(value);
            },
        []
    );

    const handleCandidateClick =
        ({ personId, jobId }: { personId?: string; jobId?: string }) =>
        () => {
            setList([{ personId, jobId }], { personId, jobId });
        };

    const handlePersonClick = (personId: string) => () => {
        setList([{ personId }], { personId });
    };

    // Optional: Custom renderer for entity cells (future: make clickable)
    const getCellRenderer = React.useMemo(() => {
        const renderers: Record<string, (value: DataPanelCellValue, row: TableRow) => string | JSX.Element> = {};

        table.columns.forEach((col) => {
            renderers[col.id] = (value: DataPanelCellValue, row: TableRow) => {
                const cellValue = row[col.id];
                if (isCellWithEntity(cellValue) && cellValue.entityId) {
                    switch (cellValue.entityType) {
                        case 'client':
                            return (
                                <PageDialogLink
                                    Component={ClientCandidates}
                                    componentProps={{ id: cellValue.entityId }}
                                    url={`/clients/${cellValue.entityId}/candidates`}
                                >
                                    {cellValue.displayValue}
                                </PageDialogLink>
                            );
                        case 'job':
                            return (
                                <PageDialogLink
                                    Component={JobCandidatesBoard}
                                    componentProps={{ jobId: cellValue.entityId }}
                                    url={`/jobs/${cellValue.entityId}/board`}
                                >
                                    {cellValue.displayValue}
                                </PageDialogLink>
                            );
                        case 'candidate':
                            return (
                                <span className="link-cell" onClick={handleCandidateClick(cellValue.entityId)}>
                                    {cellValue.displayValue}
                                </span>
                            );
                        case 'person':
                            return (
                                <span className="link-cell" onClick={handlePersonClick(cellValue.entityId)}>
                                    {cellValue.displayValue}
                                </span>
                            );
                        default:
                            return <span>{String(cellValue.displayValue)}</span>;
                    }
                }
                if (typeof cellValue === 'string') {
                    // check if it's a URL
                    const url = normalizeUrl(cellValue);
                    if (url) {
                        return (
                            <a href={url} target="_blank" rel="noopener noreferrer">
                                {cellValue}
                            </a>
                        );
                    }
                    return <span>{cellValue}</span>;
                } else {
                    return <span>{String(value)}</span>;
                }
            };
        });

        return renderers;
    }, [table.columns]);

    const headerActions = React.useMemo(
        () => [
            <Button key="download-csv" startIcon={<GetApp />} onClick={handleDownloadCSV} style={{ color: 'white' }}>
                Download CSV
            </Button>
        ],
        [handleDownloadCSV]
    );

    return (
        <DocumentTitle title={table.title}>
            <div id="container">
                <AgentResultsHeader title={table.title} actions={headerActions} />
                <div id="content" className="flex-fill">
                    <div css={dataPanelContainerStyles(theme)}>
                        <div className={`data-panel ${className}`}>
                            <DataPanel
                                columns={dataPanelColumns}
                                data={table.rows}
                                getCellValue={getCellValue}
                                getCellRenderer={getCellRenderer}
                                sortableColumns={table.sortableColumns}
                                hidePagination={table.rows.length <= TABLE_PAGE_SIZE}
                                pageSize={TABLE_PAGE_SIZE}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </DocumentTitle>
    );
};
